import React, { useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { theme, Avatar, Button, Input } from 'antd';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { useMusician, useMusicianBands, useMusicianShows, useMusicianShowComments, useMusicianShowMedia, useContributorBands } from '../../api/queries';
import { delete_musician } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import {
  ProfileLayout,
  AboutSection,
  UpcomingShowsSection,
  ShowPhotosSection,
  ShowCommentsSection,
  PeopleListSection,
  GenreTagsSection,
} from '../../components/Profile';
import ProfileSection from '../../components/Profile/sections/ProfileSection';
import type { ActionSheetItem } from '../../components/SpecActionSheet/SpecActionSheet';
import type { ShowListingProps } from '../../components/DirectoryListings/ShowListing/ShowListing';
import type { PersonItem } from '../../components/Profile';
import type { CommentProps } from '../../components/Comment/Comment';
import type { PhotoMetadata } from '../../components/PhotoGallery/PhotoOverlayInfo';
import { DefaultAvatar, ChatIcon } from '../../components/icons';
import { UserTag } from '../../components/UserTag/UserTag';
import type { UserType } from '../../components/UserTag/UserTag';
import UserTypeInfo from '../../components/UserTypeInfo/UserTypeInfo';
import SpecAlert from '../../components/SpecAlert/SpecAlert';
import SpecModal from '../../components/SpecModal/SpecModal';
import { useShare } from '../../hooks/useShare';
import { getShowDateTime } from '../../utils/showDateTime';
import '../EditProfile.css';

dayjs.extend(isSameOrAfter);

const DELETE_PASSCODE = 'deletemyprofile';

const MusicianDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { token } = theme.useToken();
  const { user } = useAuth();
  const { data: musician, isLoading, error } = useMusician(id);
  const { data: musicianBands } = useMusicianBands(id);
  const { data: musicianShows } = useMusicianShows(id);
  const { data: musicianShowComments } = useMusicianShowComments(id);
  const { data: musicianShowMedia } = useMusicianShowMedia(id);

  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [openInfoType, setOpenInfoType] = useState<UserType | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletePasscode, setDeletePasscode] = useState('');
  const [deleting, setDeleting] = useState(false);
  const toastKeyRef = useRef(0);
  const showToast = useCallback((msg: string) => { toastKeyRef.current += 1; setToastMessage(msg); }, []);
  const share = useShare(showToast);

  const fullName = musician ? `${musician.first_name} ${musician.last_name}` : '';

  // Mirrors isAdminPermission() on the backend exactly (permissions.includes('*:*')),
  // not the looser roles.includes('admin') idiom used elsewhere in the frontend -
  // this is the literal condition that unlocks the bypass server-side.
  const isAdmin = !!user?.claims?.permissions?.includes('*:*');
  const isOwner = !!musician && !!user && musician.user_id === user.uid;
  // Musician has no "admin of this record but not owner" concept the way a
  // band does, so edit and delete share the same gate.
  const canEdit = isOwner || isAdmin;
  const canDelete = isOwner || isAdmin;

  // Real check, not a static badge: only show the Show Contributor tag if
  // this musician actually contributes to at least one band.
  const { data: contributorBands } = useContributorBands(musician?.user_id, !!user);
  const isShowContributor = (contributorBands?.length ?? 0) > 0;

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeletePasscode('');
  };

  const handleDeleteMusician = async () => {
    if (!musician) return;
    setDeleting(true);
    try {
      await delete_musician(musician.id);
      queryClient.invalidateQueries({ queryKey: ['musician', musician.id] });
      queryClient.invalidateQueries({ queryKey: ['musicians'] });
      queryClient.invalidateQueries({ queryKey: ['my-musician'] });
      navigate('/home');
    } catch (err: any) {
      showToast(err.message || 'Failed to delete musician profile');
      setDeleting(false);
    }
  };

  const actionItems: ActionSheetItem[] = musician ? [
    { key: 'share', label: 'Share', onClick: () => share({ title: fullName, entityType: 'musician', entityId: id }) },
    { key: 'copy-link', label: 'Copy Link', onClick: () => { navigator.clipboard?.writeText(window.location.href).catch(() => {}); showToast('Link Copied'); } },
    { key: 'links', label: 'Links', onClick: () => navigate(`/musicians/${id}/links`) },
    { key: 'qr-code', label: 'QR Code', onClick: () => navigate(`/musician/${id}/qr`) },
    ...(canEdit ? [{ key: 'edit-profile', label: 'Edit Profile', onClick: () => navigate(`/edit-musician-profile/${id}`) }] : []),
    ...(canDelete ? [{ key: 'delete', label: 'Delete Profile', danger: true, onClick: () => setDeleteModalOpen(true) }] : []),
  ] : [];

  // Format date to "Day Month Date" format (e.g., "Wed Sep 17")
  const formatDate = (dateString: string) => {
    // Parse date parts manually to avoid timezone issues with date-only strings
    const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format timestamp to "Mon Day Time" format (e.g., "Sep 16 3:29 PM")
  const formatTimestamp = (timestamp: string | { seconds: number } | { _seconds: number } | undefined) => {
    if (!timestamp) return '';

    // Handle Firestore Timestamp object (with seconds or _seconds)
    if (typeof timestamp === 'object') {
      const seconds = 'seconds' in timestamp ? timestamp.seconds : '_seconds' in timestamp ? (timestamp as { _seconds: number })._seconds : null;
      if (seconds) {
        return dayjs.unix(seconds).format('MMM D h:mm A');
      }
      return '';
    }

    // Handle ISO string or other date string
    const parsed = dayjs(timestamp);
    if (!parsed.isValid()) return '';
    return parsed.format('MMM D h:mm A');
  };

  // Extract display name from what might be an email (use part before @)
  const formatUserName = (name: string | undefined) => {
    if (!name) return 'Anonymous';
    if (name.includes('@')) return name.split('@')[0];
    return name;
  };

  // Filter musician shows into upcoming (today and future) and sort by date
  const today = dayjs().startOf('day');

  const allShowsTransformed = (musicianShows || [])
    .map(show => ({
      id: show.id,
      bandName: show.band_name,
      date: formatDate(show.date),
      rawDate: show.date,
      venue: show.venue_name || show.address || 'TBA',
      time: show.time,
      description: show.description,
      imageUrl: show.band_photo_url,
    }))
    .sort((a, b) => getShowDateTime(a.rawDate, a.time) - getShowDateTime(b.rawDate, b.time));

  const upcomingShows = allShowsTransformed
    .filter(show => dayjs(show.rawDate).startOf('day').isSameOrAfter(today))
    .map(({ rawDate, ...show }) => show);

  // Only show upcoming shows in the main section
  const showsToDisplay = upcomingShows;

  // Transform musician bands to PersonItem format
  const bands: PersonItem[] = (musicianBands || []).map(band => ({
    name: band.name,
    avatarUrl: band.photo_url,
    avatarShape: 'rounded' as const,
    route: `/bands/${band.id}`,
  }));

  // Transform show comments to CommentProps format
  const comments: Omit<CommentProps, 'className'>[] = (musicianShowComments || []).map(comment => {
    const rawUserId = comment.user_id || comment.created_by_id;
    const userId = rawUserId && rawUserId !== 'system' ? rawUserId : undefined;
    return {
      userName: formatUserName(comment.user_name),
      avatarUrl: comment.user_avatar_url,
      timestamp: formatTimestamp(comment.timestamp || comment.created_at || ''),
      userTags: (comment.user_tags || []) as ('fan' | 'musician' | 'showContributor')[],
      text: comment.comment,
      userProfileUrl: userId ? `/fans/${userId}` : undefined,
      onUserClick: userId ? () => navigate(`/fans/${userId}`) : undefined,
      showEventInfo: !!(comment.band_name && comment.show_date && comment.venue_name),
      eventInfo: (comment.band_name && comment.show_date && comment.venue_name) ? {
        artistName: comment.band_name,
        artistAvatarUrl: comment.band_photo_url,
        date: formatDate(comment.show_date),
        venue: comment.venue_name,
      } : undefined,
    };
  });

  // Transform show media to photos array and metadata
  const photos: string[] = (musicianShowMedia || []).map(media => media.url);
  const photoMetadata: PhotoMetadata[] = (musicianShowMedia || []).map(media => {
    const rawUserId = media.user_id || media.created_by_id;
    const userId = rawUserId && rawUserId !== 'system' ? rawUserId : undefined;
    return {
      userName: formatUserName(media.user_name),
      userAvatarUrl: media.user_avatar_url,
      timestamp: formatTimestamp(media.timestamp || media.created_at || ''),
      userTags: (media.user_tags || []) as ('fan' | 'musician' | 'showContributor')[],
      userProfileUrl: userId ? `/fans/${userId}` : undefined,
      eventInfo: (media.band_name && media.show_date && media.venue_name) ? {
        artistName: media.band_name,
        artistAvatarUrl: media.band_photo_url,
        date: formatDate(media.show_date),
        venue: media.venue_name,
      } : undefined,
    };
  });

  if (isLoading) {
    return (
      <div style={{ padding: 24, fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
        Loading musician...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24, fontSize: token.fontSizeSM, color: token.colorError }}>
        Error loading musician: {error.message}
      </div>
    );
  }

  if (!musician) {
    return (
      <div style={{ padding: 24, fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
        Musician not found.
      </div>
    );
  }

  const displayName = `${musician.first_name} ${musician.last_name}`;

  return (
    <ProfileLayout
      profileType="musician"
      heroVariant="blurred"
      heroImageUrl={musician.photo_url}
      showPlayButton={!!musician.soundcloud_url}
      soundcloudUrl={musician.soundcloud_url}
      actionSheetItems={actionItems}
    >
      <div className="band-identity">
        <div className="band-identity__avatar">
          {musician.photo_url ? (
            <img className="band-identity__avatar-img band-identity__avatar-img--circle" src={musician.photo_url} alt={displayName} />
          ) : (
            <div className="band-identity__avatar-placeholder band-identity__avatar-placeholder--circle">
              <DefaultAvatar size={48} />
            </div>
          )}
        </div>
        <div className="band-identity__info">
          <h1 className="band-identity__name">{displayName}</h1>
          <div className="band-identity__badges">
            <UserTag type="musician" onClick={() => setOpenInfoType('musician')} />
            {isShowContributor && (
              <UserTag type="showContributor" onClick={() => setOpenInfoType('showContributor')} />
            )}
          </div>
        </div>
        <UserTypeInfo type="musician" open={openInfoType === 'musician'} onClose={() => setOpenInfoType(null)} />
        <UserTypeInfo type="showContributor" open={openInfoType === 'showContributor'} onClose={() => setOpenInfoType(null)} />
        {bands.length > 0 && (
          <div
            className="band-identity__members band-identity__members--rounded"
            onClick={() => document.getElementById('bands-section')?.scrollIntoView({ behavior: 'smooth' })}
          >
            <Avatar.Group size="default" max={{ count: 3, style: { backgroundColor: '#252B2D', color: '#fff', fontSize: 12, borderRadius: 4 } }}>
              {bands.map((band, index) => (
                <Avatar
                  key={index}
                  src={band.avatarUrl}
                  shape="square"
                  style={!band.avatarUrl ? { backgroundColor: '#252B2D' } : undefined}
                />
              ))}
            </Avatar.Group>
            <span className="band-identity__members-text">
              With {bands[0]?.name}{bands.length > 1 ? ' and more...' : ''}
            </span>
          </div>
        )}
      </div>

      {/* Shows */}
      <UpcomingShowsSection
        shows={showsToDisplay}
        allShows={allShowsTransformed}
        showAddButton
        addShowIcon={<ChatIcon size="s" tone="black" />}
        onAddShow={() => navigate('/add-show')}
      />

      {/* Show Photos */}
      {photos.length > 0 ? (
        <ShowPhotosSection
          photos={photos}
          photoMetadata={photoMetadata}
          subtitle="Photos from shows"
        />
      ) : (
        <ProfileSection title="Show Photos" subtitle="Photos from shows">
          <div className="view-all-drawer__empty">No photos yet</div>
        </ProfileSection>
      )}

      {/* Show Comments */}
      {comments.length > 0 ? (
        <ShowCommentsSection
          comments={comments}
          showEventInfo
        />
      ) : (
        <ProfileSection title="Show Comments">
          <div className="view-all-drawer__empty">No comments yet</div>
        </ProfileSection>
      )}

      {/* Bands */}
      {bands.length > 0 ? (
        <PeopleListSection
          id="bands-section"
          title="Bands"
          people={bands}
          avatarShape="rounded"
        />
      ) : (
        <ProfileSection title="Bands">
          <div className="view-all-drawer__empty">No bands listed</div>
        </ProfileSection>
      )}

      {/* Instruments */}
      {musician.instruments && musician.instruments.length > 0 ? (
        <GenreTagsSection title="Instruments" genres={musician.instruments} />
      ) : (
        <ProfileSection title="Instruments">
          <div className="view-all-drawer__empty">No instruments listed</div>
        </ProfileSection>
      )}

      <SpecAlert key={toastKeyRef.current} message={toastMessage} onDone={() => setToastMessage(null)} />

      {/* Delete Profile Modal */}
      <SpecModal
        open={deleteModalOpen}
        onCancel={closeDeleteModal}
        title="Delete Profile"
        footer={null}
        closable={false}
        centered
        width={340}
      >
        <p className="edit-profile-modal-body">
          Type {DELETE_PASSCODE} to permanently delete this musician profile.
          This deletes the musician profile only - the linked account is not affected.
        </p>
        <div className="edit-profile-delete-form">
          <span className="edit-profile-delete-label">Delete Passcode:</span>
          <Input
            placeholder={`Enter ${DELETE_PASSCODE} to delete`}
            value={deletePasscode}
            onChange={(e) => setDeletePasscode(e.target.value)}
          />
          <Button
            block
            size="large"
            shape="round"
            className="edit-profile-delete-btn"
            disabled={deletePasscode !== DELETE_PASSCODE || deleting}
            loading={deleting}
            onClick={handleDeleteMusician}
          >
            DELETE PROFILE
          </Button>
        </div>
      </SpecModal>
    </ProfileLayout>
  );
};

export default MusicianDetail;
